import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useClaimActionMutation } from '@/client/api/ownership-claims/ownership-claims.mutation';
import type { ClaimAction, OwnershipClaim } from '@/client/types/ownership-claims';
import { personLabel } from './claimStatus';

const COPY: Record<ClaimAction, { title: string; confirm: string; done: string; reasonRequired: boolean }> = {
  approve: { title: 'Approve claim', confirm: 'Approve & transfer', done: 'Claim approved', reasonRequired: false },
  reject: { title: 'Reject claim', confirm: 'Reject claim', done: 'Claim rejected', reasonRequired: true },
  reopen: { title: 'Reopen claim', confirm: 'Reopen claim', done: 'Claim reopened', reasonRequired: true },
  remove: { title: 'Remove claim', confirm: 'Remove claim', done: 'Claim removed', reasonRequired: true },
};

const effectOf = (action: ClaimAction, claim: OwnershipClaim): string => {
  const listing = claim.entity?.name || `listing #${claim.eid}`;
  const claimer = personLabel(claim.claimer);
  const owner = personLabel(claim.owner);
  switch (action) {
    case 'approve':
      return `“${listing}” moves from ${owner} to ${claimer}'s personal account — it leaves any organization it was in, and no organization access changes. Any other pending claims on this listing are rejected.`;
    case 'reject':
      return `${claimer} is told the claim was declined, with your reason.`;
    case 'reopen':
      return claim.status === 'approved'
        ? `Ownership of “${listing}” returns to ${owner} and the claim goes back to pending.`
        : 'The claim goes back to pending for a new decision.';
    case 'remove':
      return claim.status === 'approved'
        ? `Ownership returns to ${owner}, and the claim is removed from everyone's lists.`
        : "The claim is removed from everyone's lists. Use this for spam or abuse.";
  }
};

interface ClaimActionDialogProps {
  claim: OwnershipClaim;
  action: ClaimAction | null;
  onClose: () => void;
}

const ClaimActionDialog = ({ claim, action, onClose }: ClaimActionDialogProps) => {
  const [note, setNote] = useState('');
  const mutation = useClaimActionMutation();
  if (!action) return null;
  const copy = COPY[action];
  const missingReason = copy.reasonRequired && !note.trim();

  const close = () => {
    if (mutation.isPending) return;
    setNote('');
    onClose();
  };

  const submit = async () => {
    if (missingReason) return;
    try {
      await mutation.mutateAsync({ claimid: claim.claimid, action, note });
      toast.success(copy.done, { richColors: true });
      setNote('');
      onClose();
    } catch (error: any) {
      toast.error(`${copy.title} failed`, { description: error?.message, richColors: true });
    }
  };

  return (
    <AlertDialog open onOpenChange={(open) => !open && close()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy.title}</AlertDialogTitle>
          <AlertDialogDescription>{effectOf(action, claim)}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="claim-action-note">
            Reason {copy.reasonRequired ? <span className="text-red-500">*</span> : '(optional)'}
          </Label>
          <textarea
            id="claim-action-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder={action === 'reject' ? 'Shown to the claimant' : 'Recorded on the claim and in the audit log'}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={mutation.isPending}>Cancel</AlertDialogCancel>
          <Button
            onClick={() => void submit()}
            disabled={missingReason || mutation.isPending}
            variant={action === 'approve' || action === 'reopen' ? 'default' : 'destructive'}
          >
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {copy.confirm}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ClaimActionDialog;
